import { computed, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { dsDtos } from '@vectronic/openApi-admin';
import { cloneDeep } from 'es-toolkit';

@Injectable({ providedIn: 'root' })
export class ParameterDataService {
  parameterDataList = signal<dsDtos.ParameterData[]>([]);
  changedParameterDataList = signal<dsDtos.ParameterData[]>([]);
  changedParameterDataListWithFactoryReset = computed<dsDtos.ParameterData[]>(() => {
    const result: dsDtos.ParameterData[] = [];
    const parameterDataList = this.parameterDataList();
    const changedParameterDataList = this.changedParameterDataList();
    for (const parameterData of changedParameterDataList) {
      result.push(parameterData);
      if (parameterData.data !== undefined) continue;
      const factoryData = parameterDataList.find(
        ({ parameterId, isUserData }) => parameterId === parameterData.parameterId && !isUserData,
      );
      if (factoryData) result.push(factoryData);
    }
    return result;
  });

  updatedParameterDataList = computed<dsDtos.ParameterData[]>(() => {
    let parameterDataList = cloneDeep(this.parameterDataList()) || [];
    parameterDataList = parameterDataList.filter(
      (parameterData) =>
        !this.changedParameterDataList().some(
          (changedParameterData) =>
            parameterData.parameterId === changedParameterData.parameterId &&
            Boolean(parameterData.isUserData) === Boolean(changedParameterData.isUserData),
        ),
    );
    this.changedParameterDataList()?.forEach((changedParameterData) =>
      parameterDataList.push(cloneDeep(changedParameterData)),
    );
    return parameterDataList;
  });

  isChanged = computed(() => {
    if (!this.parameterDataList()?.length && this.changedParameterDataList()?.length) {
      return true;
    }
    return this.changedParameterDataList().some((changedParameterData) => {
      const parameterData = this.parameterDataList()?.find(
        (parameterData) =>
          changedParameterData.parameterId === parameterData.parameterId &&
          changedParameterData.isUserData === parameterData.isUserData,
      );
      return parameterData?.data !== changedParameterData.data;
    });
  });

  isParameterChanged(parameterId: string): boolean {
    const changedList = this.changedParameterDataList().filter(
      (changedParameterData) => changedParameterData.parameterId === parameterId,
    );
    if (!changedList.length) return false;
    return changedList.some((changedParameterData) => {
      const parameterData = this.parameterDataList()?.find(
        (parameterData) =>
          changedParameterData.parameterId === parameterData.parameterId &&
          Boolean(changedParameterData.isUserData) === Boolean(parameterData.isUserData),
      );
      return parameterData?.data !== changedParameterData.data;
    });
  }

  parameterDataList$ = toObservable(this.parameterDataList);
  updatedParameterDataList$ = toObservable(this.updatedParameterDataList);

  init(parameterDataList: dsDtos.ParameterData[], updateDefault = true) {
    if (updateDefault) {
      this.parameterDataList.set(parameterDataList);
      this.changedParameterDataList.set([]);
    } else {
      this.parameterDataList.set([]);
      this.changedParameterDataList.set(parameterDataList);
    }
  }

  reset() {
    this.parameterDataList.set([]);
    this.changedParameterDataList.set([]);
  }
}
